(() => {
  'use strict';
  if (window.__livearoVerticalBound) return;
  window.__livearoVerticalBound = true;

  const STORAGE_KEY = 'livearo-vertical-selections-v1';
  const all = (selector, root = document) => Array.from(root.querySelectorAll(selector));
  const within = (selector, root = document) => root.matches?.(selector) ? [root, ...all(selector, root)] : all(selector, root);
  const language = document.documentElement.lang.toLowerCase().startsWith('zh') ? 'zh'
    : document.documentElement.lang.toLowerCase().startsWith('en') ? 'en' : 'ja';
  const local = value => typeof value === 'string' ? value : String(value?.[language] || value?.ja || value?.en || '');
  const colors = new Map();
  const currentColors = new WeakMap();
  let selections = [];
  let statusTimer;

  function text(key, replacements = {}) {
    return local(window.LIVEARO_VERTICAL?.ui?.[key] || key).replace(/\{(\w+)\}/g, (match, name) =>
      Object.prototype.hasOwnProperty.call(replacements, name) ? String(replacements[name]) : match);
  }

  function readData() {
    colors.clear();
    const entries = window.LIVEARO_VERTICAL?.colors;
    if (!Array.isArray(entries)) return;
    entries.forEach(color => {
      if (typeof color?.id === 'string' && color.id && !colors.has(color.id)) colors.set(color.id, color);
    });
  }

  function validSelections(value) {
    return Array.isArray(value) ? [...new Set(value.filter(id => typeof id === 'string' && colors.has(id)))] : [];
  }

  function parseSelections(value) {
    try { return validSelections(JSON.parse(value || '[]')); }
    catch (_) { return []; }
  }

  function saveSelections() {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(selections)); }
    catch (_) { /* Keep the current selection usable when browser storage is unavailable. */ }
  }

  function loadSelections() {
    try { return parseSelections(localStorage.getItem(STORAGE_KEY)); }
    catch (_) { return []; }
  }

  window.LIVEARO_VERTICAL_SELECTIONS = () => selections.slice();

  function colorLabel(color) {
    return text('referenceLabel') + ' ' + String(color.number || '') + ' / ' + local(color.name);
  }

  function paint(node, color) {
    if (typeof color?.hex === 'string' && /^#[0-9a-f]{6}$/i.test(color.hex)) node.style.backgroundColor = color.hex;
    else node.style.removeProperty('background-color');
  }

  function announce(key, color) {
    clearTimeout(statusTimer);
    const replacements = { count: selections.length, number: color?.number || '', name: local(color?.name) };
    all('[data-vertical-status]').forEach(node => {
      node.setAttribute('role', 'status');
      node.setAttribute('aria-live', 'polite');
      node.hidden = false;
      node.textContent = text(key, replacements);
    });
    statusTimer = window.setTimeout(() => {
      all('[data-vertical-status]').forEach(node => { node.textContent = ''; });
    }, 4500);
  }

  function updateAddButtons(library) {
    const id = currentColors.get(library);
    const added = selections.includes(id);
    all('[data-vertical-add]', library).forEach(button => {
      button.textContent = text(added ? 'added' : 'add');
      button.disabled = !colors.has(id) || added;
      button.setAttribute('aria-pressed', String(added));
    });
  }

  function selectColor(library, requestedId, updateURL = false) {
    const id = colors.has(requestedId) ? requestedId : colors.keys().next().value;
    const color = colors.get(id);
    currentColors.set(library, id);
    all('[data-vertical-color]', library).forEach(button => {
      button.setAttribute('aria-pressed', String(Boolean(color) && button.dataset.verticalColor === id));
    });
    all('[data-vertical-preview]', library).forEach(node => paint(node, color));
    all('[data-vertical-selected-name]', library).forEach(node => { node.textContent = color ? colorLabel(color) : ''; });
    updateAddButtons(library);
    if (updateURL && color) {
      try {
        const url = new URL(location.href);
        url.searchParams.set('verticalColor', id);
        if (url.href !== location.href) history.replaceState(history.state, '', url);
      } catch (_) { /* Embedded previews may disallow history updates. */ }
    }
  }

  function updateInquiryLink(anchor) {
    if (selections.length && anchor.dataset.contactUrl) {
      try {
        const url = new URL(anchor.dataset.contactUrl, location.href);
        url.searchParams.set('type', 'sample');
        url.searchParams.set('verticalColors', JSON.stringify(selections));
        anchor.href = url.href;
        anchor.removeAttribute('aria-disabled');
        return;
      } catch (_) { /* An invalid base URL cannot create an enquiry link. */ }
    }
    anchor.removeAttribute('href');
    anchor.setAttribute('aria-disabled', 'true');
  }

  function renderSelections() {
    all('[data-vertical-selection-count]').forEach(node => { node.textContent = String(selections.length); });
    all('[data-vertical-selection-empty]').forEach(node => {
      node.hidden = selections.length > 0;
      node.textContent = text('noSelection');
    });
    all('[data-vertical-selection-list]').forEach(list => {
      const fragment = document.createDocumentFragment();
      selections.forEach(id => {
        const color = colors.get(id);
        if (!color) return;
        const item = document.createElement('li');
        item.className = 'vertical-selection-item';
        const swatch = document.createElement('span');
        swatch.className = 'vertical-selection-swatch';
        swatch.setAttribute('aria-hidden', 'true');
        paint(swatch, color);
        const name = document.createElement('span');
        name.className = 'vertical-selection-name';
        name.textContent = colorLabel(color);
        const remove = document.createElement('button');
        remove.type = 'button';
        remove.className = 'vertical-selection-remove';
        remove.dataset.verticalRemove = id;
        remove.textContent = text('remove');
        remove.setAttribute('aria-label', text('remove') + ' ' + colorLabel(color));
        item.append(swatch, name, remove);
        fragment.append(item);
      });
      list.replaceChildren(fragment);
    });
    all('[data-vertical-clear]').forEach(button => {
      button.disabled = !selections.length;
      button.textContent = text('clear');
    });
    all('[data-vertical-inquiry]').forEach(updateInquiryLink);
    all('[data-vertical-library]').forEach(updateAddButtons);
    document.dispatchEvent(new CustomEvent('livearo:vertical-selection-updated', { detail: { count: selections.length } }));
  }

  function prefillContact(root = document) {
    const requested = parseSelections(new URLSearchParams(location.search).get('verticalColors'));
    if (!requested.length) return;
    within('[data-contact-product]', root).forEach(input => {
      if (input.dataset.verticalPrefilled) return;
      const existing = String(input.value || '');
      const additions = requested.map(id => {
        const color = colors.get(id);
        return local(window.LIVEARO_VERTICAL?.title) + ' / ' + text('referenceLabel') + ' ' + String(color.number || '') + ' ' + local(color.name);
      }).filter(value => !existing.includes(value));
      if (additions.length) input.value = [existing, ...additions].filter(Boolean).join(' / ');
      input.dataset.verticalPrefilled = 'true';
    });
  }

  function bind(root = document) {
    readData();
    selections = validSelections(selections);
    const requested = new URLSearchParams(location.search).get('verticalColor');
    all('[data-vertical-library]').forEach(library => {
      all('button[data-vertical-color], button[data-vertical-add]', library).forEach(button => { button.type = 'button'; });
      selectColor(library, currentColors.has(library) ? currentColors.get(library) : requested);
    });
    all('button[data-vertical-clear]').forEach(button => { button.type = 'button'; });
    renderSelections();
    prefillContact(root);
  }

  function start() {
    readData();
    selections = loadSelections();
    document.addEventListener('click', event => {
      const control = event.target.closest?.('[data-vertical-color], [data-vertical-add], [data-vertical-remove], [data-vertical-clear], [data-vertical-inquiry]');
      if (!control) return;
      if (control.matches('[data-vertical-inquiry]')) {
        if (control.getAttribute('aria-disabled') === 'true') event.preventDefault();
        return;
      }
      event.preventDefault();
      if (control.disabled) return;
      const library = control.closest('[data-vertical-library]');
      if (control.matches('[data-vertical-color]')) {
        if (library && colors.has(control.dataset.verticalColor)) selectColor(library, control.dataset.verticalColor, true);
        return;
      }
      if (control.matches('[data-vertical-add]')) {
        const id = library && currentColors.get(library);
        if (!colors.has(id) || selections.includes(id)) return;
        selections.push(id);
        saveSelections(); renderSelections(); announce('addedStatus', colors.get(id));
      } else if (control.matches('[data-vertical-remove]')) {
        const id = control.dataset.verticalRemove;
        if (!selections.includes(id)) return;
        selections = selections.filter(value => value !== id);
        saveSelections(); renderSelections(); announce('removedStatus', colors.get(id));
      } else if (control.matches('[data-vertical-clear]')) {
        selections = [];
        saveSelections(); renderSelections(); announce('clearedStatus');
      }
    });
    document.addEventListener('shopify:section:load', event => bind(event.target));
    window.addEventListener('storage', event => {
      if (event.key !== STORAGE_KEY && event.key !== null) return;
      selections = event.key === null ? [] : parseSelections(event.newValue);
      renderSelections();
    });
    window.addEventListener('popstate', () => {
      const requested = new URLSearchParams(location.search).get('verticalColor');
      all('[data-vertical-library]').forEach(library => selectColor(library, requested));
    });
    bind();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
